import { supabase } from '../lib/supabase'
import { stripePlans } from '../lib/stripe'

export interface CheckoutSessionData {
  planId: string
  userId: string
  successUrl: string
  cancelUrl: string
}

export interface PaymentIntentData {
  planId: string
  userId: string
  billingInfo: {
    email: string
    name: string
    company?: string
    address: string
    city: string
    state: string
    zipCode: string
    country: string
    currency?: string
  }
  amount: number
  currency?: string
}

export interface PaymentResult {
  success: boolean
  sessionId?: string
  url?: string
  clientSecret?: string
  error?: string
}

export interface SubscriptionStatus {
  isActive: boolean
  planId: string
  currentPeriodEnd?: string
  cancelAtPeriodEnd?: boolean
  status?: string
}

class PaymentService {
  // Create a payment intent for Stripe Elements
  async createPaymentIntent(data: PaymentIntentData): Promise<PaymentResult> {
    try {
      if (!supabase) {
        throw new Error('Database not available')
      }

      const plan = stripePlans[data.planId as keyof typeof stripePlans]
      if (!plan || !plan.priceId) {
        throw new Error('Invalid plan selected')
      }

      // Store the payment attempt in database for tracking
      const { error: dbError } = await supabase
        .from('payment_attempts')
        .insert({
          user_id: data.userId,
          plan_id: data.planId,
          amount: data.amount,
          billing_email: data.billingInfo.email,
          billing_name: data.billingInfo.name,
          status: 'pending',
          created_at: new Date().toISOString()
        })

      if (dbError) {
        console.warn('Failed to log payment attempt:', dbError)
        // Don't fail the payment for logging issues
      }

      // Call backend API to create Stripe PaymentIntent
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: data.planId,
          amount: data.amount,
          billingInfo: data.billingInfo,
          userId: data.userId,
          currency: data.currency || 'usd'
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create payment intent')
      }

      return {
        success: true,
        clientSecret: result.clientSecret
      }
    } catch (error) {
      console.error('Error creating payment intent:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create payment intent'
      }
    }
  }

  // Create a checkout session
  async createCheckoutSession(data: CheckoutSessionData): Promise<PaymentResult> {
    try {
      if (!supabase) {
        throw new Error('Database not available')
      }

      const plan = stripePlans[data.planId as keyof typeof stripePlans]
      if (!plan || !plan.priceId) {
        throw new Error('Invalid plan selected')
      }

      // In a real application, this would call your backend API
      // which would create a Stripe checkout session
      // For demo purposes, we'll simulate the process
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // For demo: create a mock checkout session
      const mockSessionId = `cs_test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // In production, you would:
      // 1. Call your backend API
      // 2. Backend creates Stripe checkout session
      // 3. Return the session URL
      
      // For now, we'll simulate a successful checkout
      // and redirect to a success page
      const mockCheckoutUrl = `${data.successUrl}?session_id=${mockSessionId}&plan=${data.planId}`
      
      return {
        success: true,
        sessionId: mockSessionId,
        url: mockCheckoutUrl
      }
    } catch (error) {
      console.error('Error creating checkout session:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create checkout session'
      }
    }
  }

  // Verify payment and update subscription
  async verifyPaymentAndUpdateSubscription(paymentIntentId: string, userId: string, planId: string): Promise<PaymentResult> {
    try {
      if (!supabase) {
        throw new Error('Database not available')
      }

      // Call backend API to verify payment with Stripe
      const response = await fetch('/api/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          userId,
          planId
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Payment verification failed')
      }

      return {
        success: true
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify payment'
      }
    }
  }

  // Get subscription status
  async getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
    try {
      if (!supabase) {
        throw new Error('Database not available')
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('subscription_status, subscription_current_period_end, subscription_id')
        .eq('id', userId)
        .single()

      if (error) {
        throw error
      }

      const isActive = profile.subscription_status && profile.subscription_status !== 'freemium'
      const currentPeriodEnd = profile.subscription_current_period_end

      return {
        isActive,
        planId: profile.subscription_status || 'freemium',
        currentPeriodEnd,
        cancelAtPeriodEnd: false, // Would be determined by Stripe in production
        status: isActive ? 'active' : 'inactive'
      }
    } catch (error) {
      console.error('Error getting subscription status:', error)
      return {
        isActive: false,
        planId: 'freemium'
      }
    }
  }

  // Cancel subscription
  async cancelSubscription(userId: string): Promise<PaymentResult> {
    try {
      if (!supabase) {
        throw new Error('Database not available')
      }

      // In production, you would cancel the subscription with Stripe
      // For demo purposes, we'll just update the database
      
      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_status: 'freemium',
          subscription_id: null,
          subscription_current_period_end: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)

      if (error) {
        throw error
      }

      return {
        success: true
      }
    } catch (error) {
      console.error('Error canceling subscription:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to cancel subscription'
      }
    }
  }

  // Get billing history (mock data for demo)
  async getBillingHistory(_userId: string) {
    try {
      // In production, this would fetch from Stripe
      // For demo, return mock data
      await new Promise(resolve => setTimeout(resolve, 500))
      
      return {
        success: true,
        invoices: [
          {
            id: 'in_1234567890',
            date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            amount: 2900,
            status: 'paid',
            planName: 'Professional'
          },
          {
            id: 'in_0987654321',
            date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
            amount: 2900,
            status: 'paid',
            planName: 'Professional'
          }
        ]
      }
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch billing history'
      }
    }
  }
}

export const paymentService = new PaymentService() 